import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { usePublicPages } from "../../contexts/PublicPagesContext";
import { useTopBarSlots } from "../../contexts/TopBarSlotsContext";
import {
  useBadgeCountsQuery,
  useNotificationsQuery,
} from "../../hooks/profile/queries";
import api from "../../utils/api/axiosInstance";
import { useQuery } from "@tanstack/react-query";
import { getProfileTabFromPathname } from "../../utils/profilePaths";
import {
  FaImages,
  FaTools,
  FaThumbsUp,
  FaHeart,
  FaBookmark,
  FaHistory,
  FaTrashRestore,
  FaBell,
  FaCog,
  FaEnvelope,
  FaUserFriends,
  FaUsers,
  FaStar,
  FaReply,
  FaComment,
  FaUser,
  FaLayerGroup,
  FaIdCard,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import styles from "../../styles/components/profile/UserProfileTabs.module.css";

const PROFILE_TAB_LABELS = {
  posts: "Posts",
  setup: "Setup",
  matches: "My Matches",
  favorites: "Favorites",
  saved: "Collections",
  activity: "Activity",
  "recently-deleted": "Recently Deleted",
  notifications: "Notifications",
  settings: "Settings",
  messages: "Messages",
  requests: "Requests",
  reviews: "Reviews",
  answers: "Answers",
  comments: "Comments",
};

const NavItem = ({ to, end, icon: Icon, label, badge, onNavigate }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `${styles.sidebarFilterButton} ${
        isActive ? styles.sidebarFilterButtonActive : ""
      }`
    }
    onClick={onNavigate}
  >
    <Icon aria-hidden />
    <span className={styles.navLabel}>{label}</span>
    {badge > 0 && (
      <span className={styles.badge}>{badge > 99 ? "99+" : badge}</span>
    )}
  </NavLink>
);

const NavSection = ({ label, icon: Icon, showDivider = false, children }) => (
  <>
    {showDivider && <div className={styles.sidebarDivider} />}
    <div className={styles.sidebarCard}>
      {label && (
        <h3>
          {Icon && <Icon aria-hidden />}
          {label}
        </h3>
      )}
      <div className={styles.sidebarFilterList}>{children}</div>
    </div>
  </>
);

const useProfileNavData = () => {
  const { userId } = useParams();
  const { isOwnProfile } = useUserProfile();
  const base = `/user/${userId}`;

  const { data: badgeCounts } = useBadgeCountsQuery(
    isOwnProfile ? userId : null,
  );
  const { data: notificationData } = useNotificationsQuery(
    isOwnProfile ? userId : null,
    true,
  );

  const { data: unreadMessagesCount = 0 } = useQuery({
    queryKey: ["unreadMessagesCount", userId],
    queryFn: async () => {
      const [directRes, systemRes] = await Promise.allSettled([
        api.get("/users/me/notifications/messages/unread-count"),
        api.get("/users/me/system-messages/unread-count"),
      ]);
      const directCount =
        directRes.status === "fulfilled" ? directRes.value.data.count || 0 : 0;
      const systemCount =
        systemRes.status === "fulfilled" ? systemRes.value.data.count || 0 : 0;
      return directCount + systemCount;
    },
    enabled: isOwnProfile,
  });

  return {
    base,
    isOwnProfile,
    unreadNotifications: badgeCounts?.unreadNotifications || 0,
    favoritesCount: badgeCounts?.favoritesCount || 0,
    followRequestsCount: notificationData?.followRequests?.length || 0,
    unreadMessagesCount,
  };
};

const ProfileLeftNav = ({ onNavigate }) => {
  const { base, isOwnProfile, favoritesCount } = useProfileNavData();
  const { isPageEnabled } = usePublicPages();
  const feedEnabled = isPageEnabled("feed");

  return (
    <>
      <NavSection label="Profile" icon={FaUser}>
        {feedEnabled && (
          <NavItem
            to={base}
            end
            icon={FaImages}
            label="Posts"
            onNavigate={onNavigate}
          />
        )}
        <NavItem
          to={`${base}/setup`}
          icon={FaTools}
          label="Setup"
          onNavigate={onNavigate}
        />
      </NavSection>

      {isOwnProfile && (
        <NavSection label="Personal" icon={FaLayerGroup} showDivider>
          <NavItem
            to={`${base}/matches`}
            icon={FaThumbsUp}
            label="My Matches"
            onNavigate={onNavigate}
          />
          <NavItem
            to={`${base}/favorites`}
            icon={FaHeart}
            label="Favorites"
            badge={favoritesCount}
            onNavigate={onNavigate}
          />
          <NavItem
            to={`${base}/saved`}
            icon={FaBookmark}
            label="Collections"
            onNavigate={onNavigate}
          />
          <NavItem
            to={`${base}/activity`}
            icon={FaHistory}
            label="Activity"
            onNavigate={onNavigate}
          />
          {feedEnabled && (
            <NavItem
              to={`${base}/recently-deleted`}
              icon={FaTrashRestore}
              label="Recently Deleted"
              onNavigate={onNavigate}
            />
          )}
        </NavSection>
      )}
    </>
  );
};

const ProfileRightNav = ({ onNavigate, showLeadingDivider = false }) => {
  const {
    base,
    isOwnProfile,
    unreadNotifications,
    followRequestsCount,
    unreadMessagesCount,
  } = useProfileNavData();
  const { isPageEnabled } = usePublicPages();
  const feedEnabled = isPageEnabled("feed");

  if (!isOwnProfile) return null;

  return (
    <>
      <NavSection label="Community" icon={FaUserFriends} showDivider={showLeadingDivider}>
        <NavItem
          to={`${base}/messages`}
          icon={FaEnvelope}
          label="Messages"
          badge={unreadMessagesCount}
          onNavigate={onNavigate}
        />
        <NavItem
          to={`${base}/requests`}
          icon={FaUsers}
          label="Requests"
          badge={followRequestsCount}
          onNavigate={onNavigate}
        />
        <NavItem
          to={`${base}/reviews`}
          icon={FaStar}
          label="Reviews"
          onNavigate={onNavigate}
        />
        {feedEnabled && (
          <>
            <NavItem
              to={`${base}/answers`}
              icon={FaReply}
              label="Answers"
              onNavigate={onNavigate}
            />
            <NavItem
              to={`${base}/comments`}
              icon={FaComment}
              label="Comments"
              onNavigate={onNavigate}
            />
          </>
        )}
      </NavSection>

      <NavSection label="Account" icon={FaIdCard} showDivider>
        <NavItem
          to={`${base}/notifications`}
          icon={FaBell}
          label="Notifications"
          badge={unreadNotifications}
          onNavigate={onNavigate}
        />
        <NavItem
          to={`${base}/settings`}
          icon={FaCog}
          label="Settings"
          onNavigate={onNavigate}
        />
      </NavSection>
    </>
  );
};

const UserProfileLeftTabs = () => (
  <aside className={styles.sidebar} aria-label="Profile navigation">
    <nav aria-label="Profile sections">
      <ProfileLeftNav />
    </nav>
  </aside>
);

const UserProfileRightTabs = () => {
  const { isOwnProfile } = useUserProfile();
  if (!isOwnProfile) return null;

  return (
    <aside className={styles.sidebar} aria-label="Community and account">
      <nav aria-label="Community and account sections">
        <ProfileRightNav />
      </nav>
    </aside>
  );
};

const UserProfileMobileMenu = () => {
  const { userId } = useParams();
  const { pathname } = useLocation();
  const { leftSlotEl, setLeftSlotOccupied } = useTopBarSlots();
  const [isOpen, setIsOpen] = useState(false);
  const [pinnedToTopBar, setPinnedToTopBar] = useState(false);
  const [sentinelEl, setSentinelEl] = useState(null);
  const closeMenu = useCallback(() => setIsOpen(false), []);
  const activeTab = getProfileTabFromPathname(pathname, userId);
  const currentLabel = PROFILE_TAB_LABELS[activeTab] || "Profile";
  const showInTopBar = pinnedToTopBar && Boolean(leftSlotEl);

  useEffect(() => {
    closeMenu();
  }, [pathname, closeMenu]);

  useEffect(() => {
    if (!sentinelEl) return undefined;

    const media = window.matchMedia("(max-width: 768px)");

    const syncPinned = () => {
      if (!media.matches) {
        setPinnedToTopBar(false);
        return;
      }
      const header = document.querySelector("header");
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      setPinnedToTopBar(sentinelEl.getBoundingClientRect().top <= headerBottom + 1);
    };

    syncPinned();
    window.addEventListener("scroll", syncPinned, { passive: true });
    window.addEventListener("resize", syncPinned);
    media.addEventListener("change", syncPinned);

    return () => {
      window.removeEventListener("scroll", syncPinned);
      window.removeEventListener("resize", syncPinned);
      media.removeEventListener("change", syncPinned);
    };
  }, [sentinelEl]);

  useEffect(() => {
    setLeftSlotOccupied(showInTopBar);
    return () => setLeftSlotOccupied(false);
  }, [showInTopBar, setLeftSlotOccupied]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, closeMenu]);

  const menuButton = (
    <button
      type="button"
      className={`${styles.mobileMenuButton} ${
        showInTopBar ? styles.mobileMenuButtonInTopbar : ""
      }`}
      onClick={() => setIsOpen(true)}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      aria-label="Open profile menu"
    >
      <FaBars aria-hidden="true" />
      <span>Menu</span>
      <span className={styles.mobileMenuCurrent}>{currentLabel}</span>
    </button>
  );

  return (
    <>
      <div ref={setSentinelEl} className={styles.mobileMenuSentinel} />
      {!showInTopBar && (
        <div className={styles.mobileMenuBar}>{menuButton}</div>
      )}
      {showInTopBar &&
        createPortal(
          <div className={styles.topbarMenuInset}>{menuButton}</div>,
          leftSlotEl,
        )}
      {isOpen &&
        createPortal(
          <div className={styles.mobileDrawerRoot}>
            <div
              className={styles.mobileDrawerBackdrop}
              onClick={closeMenu}
              aria-hidden="true"
            />
            <div
              className={styles.mobileDrawerPanel}
              role="dialog"
              aria-modal="true"
              aria-label="Profile menu"
            >
              <div className={styles.mobileDrawerHeader}>
                <h2 className={styles.mobileDrawerTitle}>Profile menu</h2>
                <button
                  type="button"
                  className={styles.mobileDrawerClose}
                  onClick={closeMenu}
                  aria-label="Close profile menu"
                >
                  <FaTimes aria-hidden="true" />
                </button>
              </div>
              <nav className={styles.mobileDrawerBody} aria-label="Profile sections">
                <ProfileLeftNav onNavigate={closeMenu} />
                <ProfileRightNav onNavigate={closeMenu} showLeadingDivider />
              </nav>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export { UserProfileLeftTabs, UserProfileRightTabs, UserProfileMobileMenu };
export default UserProfileLeftTabs;
